import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Form, Row, Col, Card, Badge, Image } from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import api from './api/axiosInstance';
import defaultLogo from './assets/img/ic-img.svg';
import icApproved from './assets/img/ic-check-circle.svg';
import icRejected from './assets/img/ic-close-circle.svg';
import icRLifecycleReview from './assets/img/ic-lyfecycle-review.svg';
import icUnderReview from './assets/img/ic-under-review.svg';
import icNoCompanies from './assets/img/ic-empty-box.svg';

const CityCompaniesCard = ({ cityName, cityId }) => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderSubscription, setSortOrderSubscription] = useState('asc');
    const [sortOrderUsers, setSortOrderUsers] = useState('asc');
    const [sortOrderAccount, setSortOrderAccount] = useState('asc');
    const [sortOrderStatus, setSortOrderStatus] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);

    useEffect(() => {
        if (!cityName) return;
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                const cityLower = cityName.trim().toLowerCase();
                const filtered = res.data.filter(
                    (c) => String(c.cityRegion || '').trim().toLowerCase() === cityLower
                );
                setCompanies(filtered);
            } catch (error) {
                console.error('Error fetching city companies:', error);
            }
        };
        fetchCompanies();
    }, [cityName]);

    const filtered = companies.filter((c) =>
        String(c.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / companiesPerPage);
    const indexOfLast = currentPage * companiesPerPage;
    const indexOfFirst = indexOfLast - companiesPerPage;
    const currentCompanies = filtered.slice(indexOfFirst, indexOfLast);

    const sort = (field, getter, setOrder, currentOrder, numeric = false) => {
        const next = currentOrder === 'asc' ? 'desc' : 'asc';
        setOrder(next);
        setCompanies((prev) =>
            [...prev].sort((a, b) => {
                const A = getter(a);
                const B = getter(b);
                if (numeric) return next === 'asc' ? A - B : B - A;
                return next === 'asc'
                    ? String(A).localeCompare(String(B))
                    : String(B).localeCompare(String(A));
            })
        );
    };

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const out = []; for (let i = s; i <= e; i++) out.push(i); return out; };
        const b = 1, s = 2;
        const start = range(1, Math.min(b, totalPages));
        const end = range(Math.max(totalPages - b + 1, b + 1), totalPages);
        const sS = Math.max(Math.min(currentPage - s, totalPages - b - s * 2 - 1), b + 2);
        const sE = Math.min(Math.max(currentPage + s, b + s * 2 + 2), totalPages - b - 1);
        const items = [
            ...start,
            ...(sS > b + 2 ? ['ellipsis'] : b + 1 < totalPages - b ? [b + 1] : []),
            ...range(sS, sE),
            ...(sE < totalPages - b - 1 ? ['ellipsis'] : totalPages - b > b ? [totalPages - b] : []),
            ...end,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const approvalClass = (status) => {
        const s = String(status || '').toLowerCase();
        if (s === 'approved') return 'approved';
        if (s === 'rejected') return 'rejected';
        if (s === 'lifecycle review') return 'lifecycle-review';
        if (s === 'under review') return 'under-review';
        return '';
    };

    return (
        <Card>
            <Card.Body>
                <Row className="mb-3">
                    <Col xs={6}>
                        <h3><span class="item-number">3. </span>Companies in this city</h3>
                    </Col>
                    <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <Form.Control
                            type="text"
                            placeholder="Search companies..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            style={{ maxWidth: 260 }}
                        />
                    </Col>
                </Row>

                {filtered.length === 0 ? (
                    <Row>
                        <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center py-4">
                            <Image src={icNoCompanies} alt="No Companies" className="mb-4" />
                            <h2 className="mb-3">No companies yet.</h2>
                            <p className="mb-0">Companies assigned to this city will appear here.</p>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <Col>
                                <div className="table-responsive list">
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th className="th-fixed-company" onClick={() => sort('name', (c) => c.title || '', setSortOrderName, sortOrderName)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Company
                                                        <span>
                                                            <FaCaretUp className={sortOrderName === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderName === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th className="th-fixed-subscription" onClick={() => sort('sub', (c) => c.accessLevel || '', setSortOrderSubscription, sortOrderSubscription)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Subscription
                                                        <span>
                                                            <FaCaretUp className={sortOrderSubscription === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderSubscription === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th className="th-fixed-users" onClick={() => sort('users', (c) => c.usersCount ?? 0, setSortOrderUsers, sortOrderUsers, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Users
                                                        <span>
                                                            <FaCaretUp className={sortOrderUsers === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderUsers === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th className="th-fixed-account" onClick={() => sort('account', (c) => c.accountApprovalStatus || '', setSortOrderAccount, sortOrderAccount)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Account
                                                        <span>
                                                            <FaCaretUp className={sortOrderAccount === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderAccount === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th className="th-fixed-status" onClick={() => sort('status', (c) => c.activityStatus ? 1 : 0, setSortOrderStatus, sortOrderStatus, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Status
                                                        <span>
                                                            <FaCaretUp className={sortOrderStatus === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderStatus === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentCompanies.map((company) => (
                                                <tr
                                                    className="company-row discovery-row"
                                                    key={company._id}
                                                    onClick={() => navigate(cityId ? `/city-edit-companies/${cityId}/company/${company._id}` : `/company-edit/${company._id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <div>
                                                            <img
                                                                src={company.logo || defaultLogo}
                                                                alt={company.title}
                                                                className="round"
                                                            />
                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                <strong>{company.title}</strong>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{company.accessLevel}</td>
                                                    <td>{company.usersCount !== undefined ? company.usersCount : 'n/a'}</td>
                                                    <td className={approvalClass(company.accountApprovalStatus)}>
                                                        <span className="d-flex align-items-center gap-2">
                                                            {String(company.accountApprovalStatus || '').toLowerCase() === 'approved' && <img src={icApproved} alt="Approved" />}
                                                            {String(company.accountApprovalStatus || '').toLowerCase() === 'rejected' && <img src={icRejected} alt="Rejected" />}
                                                            {String(company.accountApprovalStatus || '').toLowerCase() === 'lifecycle review' && <img src={icRLifecycleReview} alt="Lifecycle Review" />}
                                                            {String(company.accountApprovalStatus || '').toLowerCase() === 'under review' && <img src={icUnderReview} alt="Under Review" />}
                                                            {company.accountApprovalStatus}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{company.activityStatus ? 'Active' : 'Inactive'}</Tooltip>}
                                                        >
                                                            <Badge bg={company.activityStatus ? 'active' : 'inactive'}>&nbsp;</Badge>
                                                        </OverlayTrigger>
                                                    </td>
                                                    <td className="text-end">
                                                        <Link to={cityId ? `/city-edit-companies/${cityId}/company/${company._id}` : `/company-edit/${company._id}`} className="btn btn-sm btn-outline-secondary" onClick={(e) => e.stopPropagation()}>
                                                            Edit
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>

                        <Row className="pagination-wrapper">
                            <Col className="d-flex align-items-center">
                                <span>Total {filtered.length} {filtered.length === 1 ? 'company' : 'companies'}</span>
                                <Pagination>
                                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                        <BsChevronLeft />
                                    </Pagination.Prev>
                                    {getPaginationItems().map((item, i) =>
                                        item === 'ellipsis' ? (
                                            <Pagination.Ellipsis key={`e-${i}`} disabled />
                                        ) : (
                                            <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>
                                                {item}
                                            </Pagination.Item>
                                        )
                                    )}
                                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                        <BsChevronRight />
                                    </Pagination.Next>
                                </Pagination>
                            </Col>
                            <Col className="pagination-controls">
                                <div className="pagination-controls-per-page">
                                    <Form.Select value={companiesPerPage} onChange={(e) => { setCompaniesPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                        <option value="10">10 / page</option>
                                        <option value="20">20 / page</option>
                                        <option value="40">40 / page</option>
                                    </Form.Select>
                                </div>
                                <label className="pagination-controls-goto-label" htmlFor="city-companies-goto">Go to</label>
                                <Form.Control
                                    id="city-companies-goto"
                                    className="pagination-controls-goto-input"
                                    type="number"
                                    value={currentPage}
                                    onChange={(e) => {
                                        const p = Number(e.target.value);
                                        if (p >= 1 && p <= totalPages) setCurrentPage(p);
                                    }}
                                    onBlur={() => {
                                        if (currentPage < 1) setCurrentPage(1);
                                        if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default CityCompaniesCard;
